import { useEffect, useState, useContext } from 'react';
import { ClientContext } from '../../../../contexts/clientProvider';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import { UserContext } from '../../../../contexts/userProvider';
import { getDadosGeral, getComponentMap } from '../../../General/Format/cliente';
import FormComp from '../../../General/FormsComp';

const Geral = ({ client, setClient, handleFecharClick }) => {
    const { setAlert, isGerente } = useContext(UserContext);
    const { updateCliente, estados } = useContext(ClientContext);
    const [type, setType] = useState('Geral');
    const [editedData, setEditedData] = useState({});
    const [dados, setDados] = useState({ estado: '', cidade: '' });
    const [municipios, setMunicipios] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (client) {
            setEditedData(client);
        }
    }, [client]);

    const fetchData = async (estado, retries = 3) => {
        try {
            const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${estado}/municipios`);
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Erro ao recuperar dados:', error);
            if (retries > 0) {
                return fetchData(estado, retries - 1);
            }
            return [];
        }
    };

    useEffect(() => {
        fetchData(editedData.estado).then(setMunicipios);
    }, [editedData.estado]);

    useEffect(() => {
        fetchData(client.estado).then(data => {
            if (!data) return;
            const cidade = data.find(cidade => cidade.id === parseInt(client.cidade))?.nome;
            const estadoSigla = estados.find(estado => estado.id === client.estado)?.nome;
            setDados({ estado: estadoSigla, cidade: cidade || 'Nenhum dado cadastrado.' });
        })
    }, []);

    const handleEditClick = () => {
        setType('Edit');
        setEditedData(client);
    };

    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSaveClick = async () => {
        // Cliente sem documento não trava o salvamento (NULL pós-migração de
        // unificação por CNPJ é um valor válido): só validamos formato quando
        // há dígitos informados.
        const cpf_cnpj = editedData.cpf_cnpj ? String(editedData.cpf_cnpj).replace(/\D/g, '') : '';
        let editedDataCopy = { ...editedData };
        if (cpf_cnpj) {
            let isCpf = cpf_cnpj.length <= 11;
            if (isCpf && !cpf.isValid(cpf_cnpj)) {
                setAlert({ visible: true, title: "Atenção!", placeholder: "CPF inválido.", confirm: false, type: "warning" });
                return;
            }

            if (!isCpf && !cnpj.isValid(cpf_cnpj)) {
                setAlert({ visible: true, title: "Atenção!", placeholder: "CNPJ inválido.", confirm: false, type: "warning" });
                return;
            }
            editedDataCopy.cpf_cnpj = cpf_cnpj.length > 11 ? cnpj.format(cpf_cnpj) : cpf.format(cpf_cnpj);
        }

        if (!editedData.nome) {
            setAlert({ visible: true, title: "Atenção!", placeholder: "Preencha o campo nome.", confirm: false, type: "warning" });
            return;
        }
        setLoading(true);
        setClient(await updateCliente(editedDataCopy));
        setLoading(false);
        setType('Geral');
        handleFecharClick();
    }

    const handleCancelClick = () => {
        setType('Geral');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedData((prevData) => ({ ...prevData, [name]: value }));
    };

    // Obter dados baseado no tipo (visualização ou edição)
    const getDadosParaRenderizar = () => {
        if (type === 'Geral') {
            const dadosVis = getDadosGeral(client, dados, estados, municipios);
            // Para visualização, ajustar valores vazios para mostrar "Nenhum dado cadastrado"
            return dadosVis.map(dado => ({
                ...dado,
                valor: dado.valor || 'Nenhum dado cadastrado.'
            }));
        } else {
            // Para o modo de edição, precisamos ajustar os dados de estado e cidade
            const dadosEdit = getDadosGeral(editedData, dados, estados, municipios);
            
            // Ajustar o valor do campo ativo
            const ativoField = dadosEdit.find(d => d.name === 'ativo');
            if (ativoField) {
                ativoField.valorSelect = editedData.ativo || '0';
            }
            
            // Ajustar o valor do estado para o ID
            const estadoField = dadosEdit.find(d => d.name === 'estado');
            if (estadoField) {
                estadoField.valorSelect = editedData.estado || '';
            }
            
            // Ajustar o valor da cidade para o ID
            const cidadeField = dadosEdit.find(d => d.name === 'cidade');
            if (cidadeField) {
                cidadeField.valorSelect = editedData.cidade || '';
            }
            
            return dadosEdit;
        }
    };

    const dadosGeral = getDadosParaRenderizar();

    // Componente personalizado para exibição de dados
    const DataCard = ({ titulo, valor, icon = null }) => (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-start space-x-3">
                {icon && (
                    <div className="flex-shrink-0 text-vox-blue-600 dark:text-vox-blue-400">
                        {icon}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {titulo}
                    </h3>
                    <p className="text-base font-semibold text-gray-900 dark:text-white break-words">
                        {valor || 'Nenhum dado cadastrado.'}
                    </p>
                </div>
            </div>
        </div>
    );

    // Ícones para cada campo
    const getFieldIcon = (fieldName) => {
        const icons = {
            ativo: '🟢',
            nome: '👤',
            cpf_cnpj: '📄',
            telefone: '📞',
            email: '📧',
            endereco: '📍',
            estado: '🗺️',
            cidade: '🏙️',
            observacoes: '📝'
        };
        return icons[fieldName] || '📋';
    };

    return (
        <div className="space-y-6">
            {/* Grid responsivo para os dados */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {type === 'Geral' ? (
                    // Modo visualização
                    dadosGeral.map((dado, index) => (
                        <DataCard
                            key={index}
                            titulo={dado.titulo}
                            valor={dado.valor}
                            icon={getFieldIcon(dado.name)}
                        />
                    ))
                ) : (
                    // Modo edição
                    dadosGeral.map((dado, index) => (
                        <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                            <FormComp 
                                dados={dado} 
                                handleInputChange={handleInputChange}
                                layout="modal"
                            />
                        </div>
                    ))
                )}
            </div>

            {/* Botões de ação */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                {type === 'Geral' ? (
                    <>
                        <button 
                            onClick={handleFecharClick}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center"
                        >
                            <span className="mr-2">✕</span>
                            Fechar
                        </button>
                        {isGerente && (
                        <button 
                            onClick={handleEditClick}
                            className="flex-1 sm:flex-none px-6 py-3 bg-vox-blue-600 hover:bg-vox-blue-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center"
                        >
                            <span className="mr-2">✏️</span>
                            Editar
                        </button>
                        )}
                    </>
                ) : (
                    <>
                        <button 
                            onClick={handleCancelClick}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center"
                        >
                            <span className="mr-2">✕</span>
                            Cancelar
                        </button>
                        <button 
                            onClick={handleSaveClick}
                            disabled={loading}
                            className="flex-1 sm:flex-none px-6 py-3 bg-vox-green-600 hover:bg-vox-green-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <span className="mr-2">💾</span>
                                    Salvar
                                </>
                            )}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default Geral;